import type { PolicyQuickAction } from 'korint-sdk/react';
import { ChevronDown, Zap } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { CustomQuickActionDialog } from './CustomQuickActionDialog';
import { PublishEventQuickActionDialog } from './PublishEventQuickActionDialog';
import { UpdateFieldQuickActionDialog } from './UpdateFieldQuickActionDialog';

export function PolicyQuickActions({ actions }: { actions: PolicyQuickAction[] }) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const [openAction, setOpenAction] = useState<PolicyQuickAction>();

  if (!actions.length) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" leftIcon={<Zap className="size-4" />}>
            {t('policyQuickActions.trigger')}
            <ChevronDown className="ml-2 size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {actions.map(action => {
            const Icon = action.icon;
            const label = (
              <>
                {Icon && <Icon className="mr-2 size-4" />}
                {t(`policyQuickActions.${action.id}.label`)}
              </>
            );

            return action.type === 'link' ? (
              <DropdownMenuItem key={action.id} asChild>
                <Link to={generatePath(action.path, { policyId: policy.id })}>{label}</Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem key={action.id} onSelect={() => setOpenAction(action)}>
                {label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {openAction?.type === 'publishEvent' && (
        <PublishEventQuickActionDialog
          action={openAction}
          onClose={() => setOpenAction(undefined)}
        />
      )}
      {openAction?.type === 'updateField' && (
        <UpdateFieldQuickActionDialog
          action={openAction}
          onClose={() => setOpenAction(undefined)}
        />
      )}
      {openAction?.type === 'custom' && (
        <CustomQuickActionDialog action={openAction} onClose={() => setOpenAction(undefined)} />
      )}
    </>
  );
}
